/**
 * Document Workbench API client (TASK-440).
 *
 * Thin wrapper over the TASK-439 document storage service, reached through the
 * gateway at `/api/workbench/documents/*`. The gateway proxies these to the
 * document-export service and forwards MinIO presigned-URL redirects untouched.
 *
 * NOTE on the save contract: TASK-439 persists a new version via
 * `PUT /api/workbench/documents/{docId}` (multipart `file` field), NOT a
 * `POST .../versions` endpoint. Each PUT auto-increments the version, so the
 * PUT *is* the versioning mechanism. Optimistic concurrency is opt-in via the
 * `X-Expected-Version` header (409 on a stale write).
 */

import { httpClient, httpClientUpload } from '../../lib/httpClient';

/** TASK-745: the derivation story for a generated document. */
export interface DocumentLineage {
  generated_by: 'agent' | 'user' | 'template' | null;
  source_document_id: string | null;
  source_version: number | null;
  source_task_id: string | null;
  supporting_sources: { document_id: string; version: number }[] | null;
  source_title: string | null;
  source_current_version: number | null;
  source_resolvable: boolean;
  /** Latest "Keep current" acknowledgement, pinned to the source version seen. */
  acknowledged_version: number | null;
  /**
   * TASK-876: the WIDGET this document was generated from, e.g. `CockpitPortfolioView`.
   *
   * TASK-745's lineage is document→document. `Open as spreadsheet` generates from a
   * widget reading the portfolio API, so `source_document_id` is legitimately null and
   * the banner fell into its "source unknown" branch — accurate, and useless to the
   * colleague handed the link. Null for a document→document derivation and for rows
   * predating migration 244, which keep reading "source unknown" rather than being
   * given a provenance nobody recorded.
   */
  source_widget: string | null;
  /** When the underlying data was READ (ISO). Not `created_at` — see migration 244. */
  source_as_of: string | null;
  /** Whose book this is. Stored since TASK-606; surfaced from TASK-876. */
  client_id: string | null;
  /** Resolved at read time, not copied at generation — see the backend comment. */
  client_name: string | null;
}

/** Shape returned by `GET /api/workbench/documents/{docId}`. */
export interface DocumentResponse {
  document_id: string;
  /** Presigned MinIO URL (1-hour TTL) for the requested version. */
  url: string;
  version: number;
  format: string;
  title?: string | null;
  /** TASK-745: null when no lineage was recorded (rows predating it). */
  lineage?: DocumentLineage | null;
}

/** Shape returned by `PUT /api/workbench/documents/{docId}`. */
export interface DocumentSaveResponse {
  document_id: string;
  version: number;
  format: string;
}

const WORKBENCH_BASE = '/api/workbench/documents';

/** Supported workbench formats (must match the backend `_validate_format` allowlist). */
export type WorkbenchFormat = 'docx' | 'xlsx' | 'pptx' | 'pdf';

/**
 * MIME per format — the multipart part's Content-Type must be on the
 * backend allowlist, and editors may hand back a Blob typed `application/zip`
 * (or ''), so we always re-wrap with the correct MIME.
 */
const FORMAT_MIME: Record<WorkbenchFormat, string> = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pdf: 'application/pdf',
};

/**
 * TASK-638: server-side branded generation (docx from markdown, xlsx from a
 * sheet spec) — POST /generate on the document service. The spec vocabulary
 * mirrors backend `generator/xlsx_generator.py`.
 */
export interface GenerateDocumentRequest {
  format: 'docx' | 'xlsx';
  title: string;
  markdown?: string;
  spec?: Record<string, unknown>;
  client_id?: string;
  /**
   * TASK-876: what this generation was derived from, recorded at generation time.
   * A widget source (`source_widget` + `as_of`) needs no source document — that is the
   * case `Open as spreadsheet` produces, and the case that used to record nothing at all.
   * The server rejects a lineage block that names neither kind of source.
   */
  lineage?: {
    source_document_id?: string;
    source_version?: number;
    source_task_id?: string;
    source_widget?: string;
    /** ISO instant the underlying data was READ. */
    as_of?: string;
  };
}

export interface GenerateDocumentResponse extends DocumentSaveResponse {
  title: string;
}

export async function generateWorkbenchDocument(
  body: GenerateDocumentRequest,
): Promise<GenerateDocumentResponse> {
  return httpClient.post(`${WORKBENCH_BASE}/generate`, body);
}

/** TASK-641: gallery template metadata (manifest-backed). */
export interface DocumentTemplate {
  id: string;
  title: string;
  description: string;
  file: string;
}

export async function listDocumentTemplates(): Promise<{ templates: DocumentTemplate[] }> {
  return httpClient.get(`${WORKBENCH_BASE}/templates`);
}

/** TASK-641: render a gallery template (KG-prefilled) into a new workbench doc. */
export async function createFromTemplate(body: {
  template_id: string;
  client_id?: string;
  title?: string;
}): Promise<GenerateDocumentResponse> {
  return httpClient.post(`${WORKBENCH_BASE}/from-template`, body);
}

/** TASK-606: one row in the workbench Files-tab list. */
export interface WorkbenchDocumentSummary {
  document_id: string;
  format: WorkbenchFormat;
  version: number;
  title: string | null;
  client_id: string | null;
  client_name: string | null;
  updated_at: string | null;
  /** TASK-745: derived doc whose source moved past the recorded version, unacknowledged. */
  source_stale?: boolean;
  /** TASK-736: review state AT current_version from the document_review_state
   *  view — document-scoped state + per-task aggregates; one task's decision is
   *  never promoted to global truth. Absent/zero = derived draft. */
  review_document_state?: string | null;
  review_tasks_approved?: number;
  review_tasks_in_review?: number;
  review_tasks_changes_requested?: number;
}

/** TASK-606: list the account's workbench documents (latest version each). */
export async function listWorkbenchDocuments(): Promise<{
  documents: WorkbenchDocumentSummary[];
  total: number;
}> {
  return httpClient.get(`${WORKBENCH_BASE}`);
}

/**
 * TASK-606: create a NEW workbench document. Pass a file to upload, or just a
 * format ('docx'/'xlsx'/'pptx') for a server-generated blank (TASK-638 made
 * blank xlsx server-side too).
 */
export async function createWorkbenchDocument(opts: {
  file?: File;
  format?: WorkbenchFormat;
  title?: string;
  clientId?: string;
}): Promise<DocumentSaveResponse> {
  const formData = new FormData();
  if (opts.file) formData.append('file', opts.file);
  if (opts.format) formData.append('format', opts.format);
  if (opts.title) formData.append('title', opts.title);
  if (opts.clientId) formData.append('client_id', opts.clientId);
  return httpClientUpload.post<DocumentSaveResponse>(`${WORKBENCH_BASE}`, formData, {
    headers: { 'Content-Type': undefined },
  });
}

/**
 * Fetch document metadata + a presigned download URL for the given version
 * (defaults to the latest). Use this when you also need the current `version`
 * for optimistic-concurrency saves.
 */
export async function getDocument(
  docId: string,
  version?: number,
): Promise<DocumentResponse> {
  const query = version != null ? `?version=${version}` : '';
  return httpClient.get<DocumentResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}${query}`,
  );
}

/**
 * Persist updated document content as a new version.
 *
 * @param docId           Target document id.
 * @param blob            Edited document, exported from the editor.
 * @param expectedVersion If supplied, sent as `X-Expected-Version` for
 *                        optimistic concurrency — the backend returns 409 if
 *                        the latest version has moved on since it was read.
 * @param format          Document format ('docx' default, 'xlsx' for Excel) —
 *                        selects the filename extension + Content-Type MIME.
 * @returns The saved document's new version metadata.
 */
export async function saveDocument(
  docId: string,
  blob: Blob,
  expectedVersion?: number,
  format: WorkbenchFormat = 'docx',
): Promise<DocumentSaveResponse> {
  const formData = new FormData();
  // Field name MUST be "file" — see TASK-439 put_document(file: UploadFile).
  // Re-wrap with the format's MIME so the multipart part's Content-Type passes
  // the backend allowlist (editors may emit application/zip or '').
  const file = new File([blob], `${docId}.${format}`, { type: FORMAT_MIME[format] });
  formData.append('file', file);

  // Setting Content-Type to `undefined` lets the browser generate the
  // `multipart/form-data; boundary=…` header itself — the explicit string would
  // omit the boundary and risk a 400. Mirrors the upload idiom in amlService.ts.
  const headers: Record<string, string | undefined> = {
    'Content-Type': undefined,
  };
  if (expectedVersion != null) {
    headers['X-Expected-Version'] = String(expectedVersion);
  }

  return httpClientUpload.put<DocumentSaveResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}`,
    formData,
    { headers },
  );
}

/** TASK-740: one row in the version-history list. Attribution fields are null
 * for rows predating migration 216 — render "unknown", never a guessed actor. */
export interface DocumentVersionInfo {
  version: number;
  created_at: string | null;
  created_by: string | null;
  created_by_label?: string | null;
  created_by_kind: string | null;
  source: string | null;
  /** TASK-740: which version a restore came from; null unless source='restore'
   *  (and null for restores predating migration 223). */
  restored_from_version?: number | null;
  is_current: boolean;
}

export interface DocumentVersionsResponse {
  document_id: string;
  current_version: number;
  versions: DocumentVersionInfo[];
  total: number;
}

/** TASK-740: version history, newest-first (capped server-side; default 50). */
export async function listDocumentVersions(
  docId: string,
  limit?: number,
): Promise<DocumentVersionsResponse> {
  const query = limit != null ? `?limit=${limit}` : '';
  return httpClient.get<DocumentVersionsResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/versions${query}`,
  );
}

/**
 * TASK-740: write an old version's bytes forward as a NEW version
 * (`source='restore'`) — history is never rewritten. `expectedVersion` is
 * mandatory: the server 409s on a concurrent save rather than silently forking.
 */
export async function restoreDocumentVersion(
  docId: string,
  version: number,
  expectedVersion: number,
): Promise<DocumentSaveResponse> {
  return httpClient.post<DocumentSaveResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/versions/${version}/restore`,
    undefined,
    { headers: { 'X-Expected-Version': String(expectedVersion) } },
  );
}

/** A locator-addressed operation applied server-side (TASK-452/639). */
export interface DocumentOp {
  /** docx op kind: 'set_text' | 'set_checkbox' | 'insert_image' | 'apply_watermark'
   * | 'insert_paragraph_after' | 'insert_heading' | 'insert_table' | 'delete_block'
   * | 'set_style'. */
  type: string;
  /** Slot/anchor address (required for everything except apply_watermark). */
  locator?: unknown;
  /** Op-specific payload (string / boolean / object). */
  value?: unknown;
}

/** An xlsx op applied server-side via openpyxl (TASK-639) — mirrors xlsx_ops.XlsxOp. */
export interface SpreadsheetServerOp {
  op: 'set_range' | 'set_formula' | 'format_range' | 'insert_rows' | 'add_sheet';
  sheet?: string;
  ref?: string;
  values?: (string | number | null)[][];
  formula?: string;
  fmt?: string;
  row?: number;
  count?: number;
  name?: string;
}

/**
 * Apply a batch of ops server-side, saving a new version — docx (python-docx)
 * or xlsx (openpyxl), decided by the stored document's format. Atomic: one bad
 * op → 422 and nothing is written.
 *
 * @param expectedVersion Sent as `X-Expected-Version`; 409 if the version moved on.
 * @returns The new version metadata (re-fetch the document to reload the editor).
 */
export async function applyDocumentOps(
  docId: string,
  ops: DocumentOp[] | SpreadsheetServerOp[],
  expectedVersion?: number,
): Promise<DocumentSaveResponse> {
  const headers: Record<string, string> = {};
  if (expectedVersion != null) {
    headers['X-Expected-Version'] = String(expectedVersion);
  }
  return httpClient.post<DocumentSaveResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/apply-ops`,
    { ops },
    { headers },
  );
}

/**
 * Persist a reviewed bank-form mapping as this `(bank_id, form_type)`'s template
 * (TASK-448). After a form is filled and reviewed, the confirmed `slot_id → kg_path`
 * map is saved so the next fill of the same form is an instant template hit (no LLM).
 */
export async function confirmFormTemplate(
  docId: string,
  body: { bank_id: string; form_type: string; slot_mapping: Record<string, string> },
): Promise<{ bank_id: string; form_type: string; slot_count: number }> {
  return httpClient.post(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/map-slots/confirm`,
    body,
  );
}

// ============================================================================
// TASK-744: document reviews — submit / approve / request-changes + state feed
// ============================================================================

export interface ReviewFeedbackItem {
  /** Stable id — server-generated when absent; agent edits reference these. */
  id?: string;
  text: string;
  locator?: Record<string, unknown>;
}

export interface ReviewFeedback {
  schema_version: 1;
  summary: string;
  items: ReviewFeedbackItem[];
}

export interface DocumentReviewRow {
  id: string;
  task_id: string | null;
  document_id: string;
  document_version: number;
  decision: 'submitted' | 'approved' | 'changes_requested';
  responds_to: string | null;
  feedback: ReviewFeedback | null;
  approval_note: string | null;
  actor: string;
  actor_kind: 'human' | 'agent' | 'system';
  actor_user_id: string | null;
  created_at: string | null;
}

export type DocumentReviewStateName = 'draft' | 'in_review' | 'approved' | 'changes_requested';

/** Server-derived pre-flight for the decision buttons: mirrors the write
 *  path's authorization (human-only, SoD) so the UI can disable with a stated
 *  reason instead of surfacing a 403 after the click. Advisory only. */
export interface ReviewDecisionEligibility {
  can_decide: boolean;
  own_submission: boolean;
  reason: string | null;
}

/** One row of the document_review_state view — THE derivation, never re-derived client-side. */
export interface DocumentReviewStateRow {
  document_version: number;
  task_id: string | null;
  submission_id: string | null;
  submitted_at: string | null;
  response_id: string | null;
  response_decision: string | null;
  responded_at: string | null;
  state: DocumentReviewStateName;
  /** Present on `in_review` rows only (TASK-744 deep-audit closure). */
  decision_eligibility?: ReviewDecisionEligibility;
}

export interface DocumentReviewsResponse {
  document_id: string;
  current_version: number;
  reviews: DocumentReviewRow[];
  states: DocumentReviewStateRow[];
}

export async function listDocumentReviews(docId: string): Promise<DocumentReviewsResponse> {
  return httpClient.get<DocumentReviewsResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/reviews`,
  );
}

export interface DocumentReviewResponse {
  success: boolean;
  review: DocumentReviewRow;
}

export async function submitDocumentReview(
  docId: string,
  documentVersion: number,
  taskId?: string | null,
): Promise<DocumentReviewResponse> {
  return httpClient.post<DocumentReviewResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/reviews/submit`,
    { document_version: documentVersion, task_id: taskId ?? null },
  );
}

export async function approveDocumentReview(
  docId: string,
  documentVersion: number,
  taskId?: string | null,
  approvalNote?: string,
): Promise<DocumentReviewResponse> {
  return httpClient.post<DocumentReviewResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/reviews/approve`,
    {
      document_version: documentVersion,
      task_id: taskId ?? null,
      approval_note: approvalNote || null,
    },
  );
}

export async function requestDocumentChanges(
  docId: string,
  documentVersion: number,
  taskId: string | null,
  feedback: ReviewFeedback,
): Promise<DocumentReviewResponse> {
  return httpClient.post<DocumentReviewResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/reviews/request-changes`,
    { document_version: documentVersion, task_id: taskId ?? null, feedback },
  );
}

// ============================================================================
// TASK-741: export a stored version as a deliverable
// ============================================================================

export interface ExportVersionResponse {
  export_id: string;
  status: string;
  format: string;
  source_document_id: string;
  source_document_version: number;
  download_href: string;
}

/**
 * Export a SPECIFIC stored version into the deliverable store. Same-format is
 * a byte snapshot (the bytes the RM approved are the bytes that ship); 'pdf'
 * renders through the export pipeline. `expectedVersion` guards a concurrent
 * save (409) when exporting the current version.
 */
export async function exportDocumentVersion(
  docId: string,
  version: number,
  format: 'same' | 'pdf' = 'same',
  expectedVersion?: number,
): Promise<ExportVersionResponse> {
  const headers: Record<string, string> = {};
  if (expectedVersion != null) headers['X-Expected-Version'] = String(expectedVersion);
  return httpClient.post<ExportVersionResponse>(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/versions/${version}/export`,
    { format },
    { headers },
  );
}

// ============================================================================
// TASK-745: lineage acknowledgement + skipped-proposal audit
// ============================================================================

/**
 * "Keep current version" — an acknowledgement pinned to the observed source
 * version, never a permanent dismissal (a later source save re-raises it).
 */
export async function acknowledgeLineage(
  docId: string,
  sourceDocumentId: string,
  acknowledgedVersion: number,
): Promise<{ success: boolean }> {
  return httpClient.post(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/lineage/ack`,
    { source_document_id: sourceDocumentId, acknowledged_version: acknowledgedVersion },
  );
}

/** Record that the advisor skipped an agent proposal (audit; no content change). */
export async function skipProposal(
  docId: string,
  body: { reason?: string; proposal?: string } = {},
): Promise<{ success: boolean }> {
  return httpClient.post(
    `${WORKBENCH_BASE}/${encodeURIComponent(docId)}/proposals/skip`,
    body,
  );
}
